import Image from 'next/image'
import { cn } from '@/utils/styles/cn'
import { FanReveal } from '@/features/landing-page/v3/fan-reveal'
import { SuperscaleMark } from '@/features/landing-page/v3/superscale-mark'

/**
 * lp-v3 above-footer showcase — three product cards fanned in the centre (bottoms tucked behind the
 * orange footer) over a scatter of ambient Superscale brand marks. SERVER component: all imagery and
 * marks ship in the static HTML. The fan-out entrance is progressive enhancement (see FanReveal +
 * the `[data-fan-*]` / `.lp-mark-float` rules in globals.css); marks drift with a CSS-only loop.
 *
 * The peek-behind-footer effect is a negative bottom margin here (pulls the footer up) plus the
 * footer's own `relative z-10` (so it paints over the card bottoms). Rendered home-only for now.
 */

/** A single ambient brand mark: position (top + left OR right, as a CSS length/`calc`), pixel size,
 *  base rotation and float timing. Some are hidden on mobile to keep the small composition uncluttered. */
type BrandMark = {
    top: string
    left?: string
    right?: string
    size: number
    rotation: number
    durationSeconds: number
    delaySeconds: number
    hideOnMobile?: boolean
}

const BRAND_MARKS: BrandMark[] = [
    // Large mark — horizontally centred above the fan (`calc(50% - size/2)`); kept off the top edge so
    // its float animation isn't clipped.
    { top: '7%', left: 'calc(50% - 28px)', size: 56, rotation: 6, durationSeconds: 7, delaySeconds: 0 },
    { top: '30%', left: '3%', size: 26, rotation: -12, durationSeconds: 8, delaySeconds: 0.6 },
    { top: '18%', left: '24%', size: 34, rotation: 8, durationSeconds: 6.5, delaySeconds: 1.1, hideOnMobile: true },
    { top: '46%', left: '11%', size: 40, rotation: -6, durationSeconds: 7.5, delaySeconds: 0.3 },
    { top: '56%', left: '26%', size: 28, rotation: 18, durationSeconds: 8.5, delaySeconds: 0.9, hideOnMobile: true },
    { top: '34%', right: '6%', size: 30, rotation: -10, durationSeconds: 7, delaySeconds: 1.4 },
    { top: '58%', right: '18%', size: 24, rotation: 12, durationSeconds: 6, delaySeconds: 0.5 },
]

export function LpAboveFooterSection() {
    return (
        <section className="relative overflow-visible bg-background px-[21px] pt-[110px] md:px-10 md:pt-[150px] lg:px-[60px]">
            {/* Ambient brand marks — decorative, clipped to the section, behind the cards */}
            <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
                {BRAND_MARKS.map((mark, index) => (
                    <span
                        key={index}
                        className={cn(
                            'lp-mark-float absolute block text-[#FF5500]',
                            mark.hideOnMobile && 'hidden md:block'
                        )}
                        style={
                            {
                                top: mark.top,
                                left: mark.left,
                                right: mark.right,
                                width: mark.size,
                                height: mark.size,
                                '--mark-rot': `${mark.rotation}deg`,
                                '--mark-dur': `${mark.durationSeconds}s`,
                                '--mark-delay': `${mark.delaySeconds}s`,
                            } as React.CSSProperties
                        }
                    >
                        <SuperscaleMark className="h-full w-full" />
                    </span>
                ))}
            </div>

            {/* Fanned product cards. Final fanned transform is the server-rendered default; FanReveal
                plays the fan-out on scroll-in. Negative bottom margin tucks card bottoms behind the footer. */}
            <FanReveal className="relative z-[1] mx-auto -mb-[70px] h-[250px] w-full max-w-[760px] md:-mb-[150px] md:h-[460px]">
                {/* Left — prompt card (behind) */}
                <div className="absolute bottom-0 left-1/2 z-20 w-[132px] -translate-x-1/2 md:w-[270px]">
                    <div
                        data-fan-card
                        data-fan-i="1"
                        className="origin-bottom will-change-transform [transform:translateX(-44px)_translateY(18px)_rotate(-8deg)] md:[transform:translateX(-178px)_translateY(34px)_rotate(-8deg)]"
                    >
                        <Image
                            src="/assets/landing-v3/fan-prompt.png"
                            alt="Superscale prompt: break down AG1's top-performing paid social ads"
                            width={758}
                            height={939}
                            sizes="(max-width: 768px) 34vw, 270px"
                            className="h-auto w-full"
                        />
                    </div>
                </div>

                {/* Right — model card (behind) */}
                <div className="absolute bottom-0 left-1/2 z-10 w-[140px] -translate-x-1/2 md:w-[284px]">
                    <div
                        data-fan-card
                        data-fan-i="2"
                        className="origin-bottom will-change-transform [transform:translateX(46px)_translateY(16px)_rotate(9deg)] md:[transform:translateX(184px)_translateY(32px)_rotate(9deg)]"
                    >
                        <Image
                            src="/assets/landing-v3/fan-model.png"
                            alt="Marketer featured in a Superscale campaign creative"
                            width={372}
                            height={473}
                            sizes="(max-width: 768px) 36vw, 284px"
                            className="h-auto w-full"
                        />
                    </div>
                </div>

                {/* Middle — competitive-intelligence report card (front). The blue-outlined variant
                    from the Context & Research step is the canonical version of this asset now — it
                    replaced the older fan-report.png that shipped without the blue backdrop. */}
                <div className="absolute bottom-0 left-1/2 z-30 w-[152px] -translate-x-1/2 md:w-[300px]">
                    <div
                        data-fan-card
                        data-fan-i="0"
                        className="origin-bottom will-change-transform [transform:rotate(-1deg)]"
                    >
                        <Image
                            src="/assets/landing-v3/product-steps/context-card.png"
                            alt="Superscale competitive intelligence analysis report"
                            width={1073}
                            height={1679}
                            sizes="(max-width: 768px) 40vw, 300px"
                            className="h-auto w-full"
                        />
                    </div>
                </div>
            </FanReveal>
        </section>
    )
}
